// Previous sidebar retained for the SKE-5100 opt-in rollout.
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../components/ui/tooltip";

import { openCreatePostModal } from "../../../features/post";
import { useSelectedAccounts } from "../../../entities/accounts";
import { cn } from "../../../lib/utils";
import { useTrackEvent } from "../../../libs/analytics";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { InstagramCampaignMenuEntry } from "../../../features/instagram-campaign/InstagramCampaignMenuEntry";

import HomeIcon from "./icons/home-03.svg";
import CreatePostIcon from "../../../assets/icons/create-post.svg";
import LibIcon from "./icons/library-v2.svg";
import TemplatesIcon from "./icons/templates.svg";
import PlannerIcon from "./icons/planner.svg";
import CalendarIcon from "./icons/calendar.svg";
import IdeaPlannerIcon from "./icons/workshop.svg";
import BulkUploadIcon from "./icons/bulk-upload.svg";
import InsightsIcon from "./icons/insights.svg";
import SocialListeningIcon from "./icons/social-listening.svg";
import BrandKitsIcon from "./icons/brand-kits.svg";
import SkedlinkIcon from "./icons/skedlilnk.svg";
import SkedSocialIcon from "./icons/sked-social.svg";
import InboxIcon from "./icons/inbox.svg";
import ExternalPreviewIcon from "./icons/external-preview.svg";
import CustomReportsIcon from "./icons/custom-reports.svg";
import { getUser } from "../../../selectors/commonSelectors";
import { getAccounts } from "../../../selectors/skedCoreSelectors";
import { getLocalStoragePlannerSelectedAccount } from "../../../libs/storage/adapters";
import { COMMON_CLEAN_UP_LOADING } from "../../../constants/ActionTypes";
import { User } from "../../../features/user/types";
import { openSocialAccountConnectDialog } from "../../../features/social-account-connect/components/social-account-connect";
import { filterAccountsByPlatformType } from "../../../utils/accounts";
import { addInstagramSuggestionModal } from "../../planner/__redux-model";

import {
  UPGRADE_APPROVAL_VIEWED,
  UPGRADE_LABELS_REPORTING,
  UPGRADE_REPORT_BUILDER_VIEWED,
} from "../../../constants/Events";
import { useScrollMoreHook } from "./hooks/scroll-more-hook";
import { useCollapseSubMenu } from "./hooks/collapse-sub-menu-hook";
import { SubMenuProvider } from "./hooks/sub-menu-context";
import { goToManagePlan } from "../../../utils/manage-plan";
import {
  hasApprovals,
  hasExternalApprovals,
  needsLabelsReportingUpsell,
} from "../../../utils/feature-gates";
import {
  ApprovalTooltipWrapper,
  openSupportForEnableApprovals,
} from "../../home/utils";
import { isUpsellRequiredForApproval } from "../../../utils/user";
import { slPermission } from "../../../utils/sl-permission";
import { hasGa4SettingsAccess } from "../../../features/account-groups-form/ga4-domain-gate";
import { AccountType } from "../../../shared/types/accounts";
import {
  entitlementFeatureToggle,
  FeatureName,
  localStorageFeatureToggle,
} from "../../../utils/feature-toggle-utils";
import { openCreatePostModalV2 } from "../../../features/post/bulk-create/bulk-create-post.actions";
import {
  registerBulkCreatePostModals,
  useAndOpenRequestedPosts,
} from "../../../features/post/bulk-create/bulk-create-post.model";
import { entitlementsPermission } from "../../../utils/entitlements-permission";
import {
  insightsPath,
  isInsightsSubpathActive,
  makeInsightsNavHandler,
  type InsightsPreviewRolloutState,
  useInsightsPreviewRolloutState,
} from "../../insights-preview/insights-preview-rollout";
import { useApprovalLinks } from "../../approvals/approval-queries";
import {
  INBOX_V2_GET_BOARDS_CACHE_KEY,
  realInboxV2DataSource,
} from "../../../@inbox-v2/data/real-api/data-source";
import { readInboxV2ApiCache } from "../../../@inbox-v2/data/real-api/api-cache";
import {
  getInboxV2BoardPath,
  INBOX_V2_BOARD_ROOT_PATH,
  isInboxV2BoardSubmenuPathActive,
  isInboxV2PinnedBoardPathActive,
} from "../../../@inbox-v2/board-routes";
import { subscribeInboxV2BoardsChanged } from "../../../@inbox-v2/board-events";
import type { InboxV2BoardsPayload } from "../../../@inbox-v2/types";
import { PAGES } from "../../../constants/Pages";
import {
  buildPostsRoute,
  getPostsViewFromPath,
} from "../../../components/Posts/posts-deep-link";
import { getStoredPostsView } from "../../../components/Posts/PostsTopnav/posts-session-state";

/**
 * Menu Link component with support for custom active state detection.
 * This is a local replacement for the deprecated router-bridge Link.
 */
interface LinkProps {
  to?: string;
  checkActiveFunc?: (path: string) => boolean;
  children: React.ReactNode;
  className?: string;
  id?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  target?: string;
  "data-testid"?: string;
  "data-id"?: string;
}

export function isMenuLinkActive(currentPath: string, to?: string) {
  return to ? currentPath.includes(to.split("?")[0]) : false;
}

function Name({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        "menu-label font-inter w-full whitespace-nowrap text-sm font-medium leading-5 text-[#676571] no-underline",
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
}

const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { to, className, children, checkActiveFunc, onClick, ...props },
  ref
) {
  const location = useLocation();

  const isActive = useMemo(() => {
    const currentPath = location.pathname;
    if (checkActiveFunc) {
      return checkActiveFunc(currentPath);
    }
    // Remove query param if exist
    return isMenuLinkActive(currentPath, to);
  }, [location.pathname, to, checkActiveFunc]);

  const combinedClassName =
    `${className || ""} ${isActive ? "active" : ""}`.trim();

  // If no `to` prop, render as a button-like anchor
  if (!to) {
    return (
      <a ref={ref} {...props} onClick={onClick} className={combinedClassName}>
        {children}
      </a>
    );
  }

  return (
    <RouterLink
      ref={ref}
      to={to}
      {...props}
      onClick={onClick}
      className={combinedClassName}
    >
      {children}
    </RouterLink>
  );
});

interface MenuComponentProps {
  className?: string;
  url?: string;
}

interface MenuItemLinkProps extends LinkProps {
  icon?: JSX.Element;
  iconWrapperClassName?: string;
  labelClassName?: string;
}

interface MenuSectionProps {
  id: string;
  title: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

// Context for menu pinned state
const MenuPinnedContext = React.createContext<{
  isPinned: boolean;
  isHovered: boolean;
  togglePinned: () => void;
}>({
  isPinned: false,
  isHovered: false,
  togglePinned: () => {},
});

export const getSelectedAccountsForNewPost = (props?: {
  isPlannerPage: boolean;
  allAccounts: AccountType[];
  selectedAccounts: AccountType[];
}): AccountType[] => {
  const {
    isPlannerPage = false,
    allAccounts = [],
    selectedAccounts = [],
  } = props || {};

  if (!isPlannerPage) return selectedAccounts;

  const plannerSelectedAccount = getLocalStoragePlannerSelectedAccount();
  const selectedIGAccounts = selectedAccounts.filter(
    filterAccountsByPlatformType("IG")
  );

  // Try to match plannerSelectedAccount from selectedIGAccounts first
  if (selectedIGAccounts.length > 0 && plannerSelectedAccount) {
    const matched = selectedIGAccounts.filter(
      (account) => account._id === plannerSelectedAccount
    );
    if (matched.length > 0) {
      return matched;
    }
  }

  const IGAccounts = allAccounts.filter(filterAccountsByPlatformType("IG"));

  // Fall back to matching from all IG accounts if not found in selectedIGAccounts
  if (IGAccounts.length > 0 && plannerSelectedAccount) {
    const matched = IGAccounts.filter(
      (account) => account._id === plannerSelectedAccount
    );
    if (matched.length > 0) {
      return matched;
    }
  }

  // Fall back to first available IG account
  if (selectedIGAccounts.length > 0) return selectedIGAccounts.slice(0, 1);

  if (IGAccounts.length > 0) return IGAccounts.slice(0, 1);

  return [];
};

function getReportBuilderUpsellState(user: User, previewActive: boolean) {
  const upsellReportBuilder =
    !entitlementsPermission.hasCustomReportAccess(user);

  return {
    upsellReportBuilder,
    reportBuilderUrl: upsellReportBuilder
      ? insightsPath("/report-builder/upgrade?from=sidebar", previewActive)
      : insightsPath("/report-builder?from=sidebar", previewActive),
  };
}

// A component (not a render helper) so it can read the current location for the
// full-page-vs-client navigation decision.
function ReportBuilderV2MenuItem({
  upsellReportBuilder,
  reportBuilderUrl,
  user,
  trackEvent,
}: {
  upsellReportBuilder: boolean;
  reportBuilderUrl: string;
  user: User;
  trackEvent: ReturnType<typeof useTrackEvent>;
}) {
  return (
    <MenuItem
      icon={<CustomReportsIcon />}
      id="navigation-insights-v2-report-builder"
      to={reportBuilderUrl}
      checkActiveFunc={(path) =>
        isInsightsSubpathActive(path, "/report-builder")
      }
      onClick={makeInsightsNavHandler(reportBuilderUrl, () => {
        if (upsellReportBuilder) {
          trackEvent({
            eventName: UPGRADE_REPORT_BUILDER_VIEWED,
            useServices: ["AMP", "HS"],
            metadata: {
              plan: user?.plan,
            },
          });
        }
      })}
    >
      <UpsellTextWrapped text="Custom Reports" upsell={upsellReportBuilder} />
    </MenuItem>
  );
}

function MenuHeader({ onCreatePost }: { onCreatePost: () => void }) {
  return (
    <>
      <SkedSocial>
        <SkedSocialIcon />
        <Name className="pl-0 font-gotham text-base font-bold leading-5 text-[#333333]">
          Sked Social
        </Name>
      </SkedSocial>
      <CreatePostButton
        data-testid="create-post-button"
        onClick={onCreatePost}
        id="navigation-create-post"
      >
        <CreatePostIcon />
        <Name className="font-gotham pl-0 text-xs font-bold leading-5 tracking-[0.54px] text-white">
          Create Post
        </Name>
      </CreatePostButton>
    </>
  );
}

function MenuSection({ id, title, children, style }: MenuSectionProps) {
  return (
    <div id={id} className="flex w-full flex-col gap-1" style={style}>
      <div className="menu-group-heading mt-4 mb-1 h-auto w-[calc(var(--menu-width-max)-20px)]">
        <span className="font-inter block whitespace-nowrap pl-3 text-[10px] font-medium leading-[14px] uppercase text-[#808080]">
          {title}
        </span>
        <hr className="hidden" />
      </div>
      <hr className="menu-mini-divider m-0 w-8 border-none" />
      <div className="menu-items flex w-full flex-col gap-1 rounded-lg bg-transparent p-0">
        {children}
      </div>
    </div>
  );
}

function MenuFooterToggle({
  isPinned,
  togglePinned,
}: {
  isPinned: boolean;
  togglePinned: () => void;
}) {
  const tooltipLabel = isPinned ? "Hide menu" : "Keep menu open";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <MenuToggleButton
          aria-label={tooltipLabel}
          aria-pressed={isPinned}
          data-testid="menu-footer-toggle"
          onClick={togglePinned}
          type="button"
        >
          <ToggleIcon isPinned={isPinned} />
        </MenuToggleButton>
      </TooltipTrigger>
      <TooltipContent side="right">{tooltipLabel}</TooltipContent>
    </Tooltip>
  );
}

const SubMenuContainer = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode;
    className?: string;
    isMenuExpanded: boolean;
    maxHeight: number;
    opened: boolean;
  }
>(function SubMenuContainer(
  { children, className, isMenuExpanded, maxHeight, opened },
  ref
) {
  return (
    <div
      ref={ref}
      className={cn(
        "relative flex flex-col gap-0 overflow-hidden before:pointer-events-none before:absolute before:top-0 before:bottom-0 before:left-[22px] before:w-px before:bg-neutral-200 before:content-[''] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400",
        className
      )}
      style={{
        maxHeight: opened ? maxHeight : 0,
        overflowY: opened ? "auto" : "hidden",
        transition: isMenuExpanded ? "max-height 0.3s ease-in-out" : undefined,
      }}
    >
      {children}
    </div>
  );
});

function MenuComponent(props: MenuComponentProps) {
  const dispatch = useDispatch();
  const trackEvent = useTrackEvent();
  const accounts = useSelectedAccounts();
  const allAccounts = useSelector(getAccounts);
  const user: User = useSelector(getUser);
  const { data: stateLinks = [] } = useApprovalLinks();
  const insightsRollout = useInsightsPreviewRolloutState();
  const showInboxComments = true;
  const isBulkHidden = user?.preferences?.hideBulkUpload;
  entitlementFeatureToggle.notifyUser(user);
  useAndOpenRequestedPosts({ dispatch, user });

  const handleCreatePost = () => {
    const isPlannerPage =
      window.location.pathname.startsWith("/dashboard/planner") ||
      window.location.pathname.startsWith("/dashboard/grid-preview");
    const selectedAccountsForNewPost = getSelectedAccountsForNewPost({
      isPlannerPage,
      allAccounts: allAccounts as AccountType[],
      selectedAccounts: accounts,
    });

    if (accounts.length === 0 && allAccounts.length === 0) {
      openSocialAccountConnectDialog();
      return;
    }

    if (allAccounts.length === 0) {
      dispatch(addInstagramSuggestionModal.open as any);

      return;
    }

    dispatch({ type: COMMON_CLEAN_UP_LOADING });
    if (entitlementFeatureToggle.get("bulk-create")) {
      dispatch(
        openCreatePostModalV2({
          accounts: selectedAccountsForNewPost,
        })
      );

      return;
    }
    dispatch(
      openCreatePostModal({
        accounts: selectedAccountsForNewPost,
      })
    );
  };
  // const handleUsersClick = () => {
  //   trackEvent({ eventName: USER_PAGE_NAVIGATION });
  // };

  useEffect(() => {
    /** ignore if user is not loadded yet */
    if (!user?.userId) {
      return;
    }

    sessionStorage.setItem("isInboxEnabled", "true");

    if (showInboxComments) {
      sessionStorage.setItem("isInboxCommentsEnabled", "true");
    } else {
      sessionStorage.removeItem("isInboxCommentsEnabled");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!user?.userId, stateLinks.length]);

  useEffect(() => {
    registerBulkCreatePostModals();
  }, []);

  const { onContainerScroll, scrollContainerRef } = useScrollMoreHook();

  const upsellApproval =
    isUpsellRequiredForApproval(user) && stateLinks.length <= 0;

  const { upsellReportBuilder, reportBuilderUrl } = getReportBuilderUpsellState(
    user,
    insightsRollout.previewActive
  );
  const { isPinned, setPinned } = useMenuPinned();
  const [isHovered, setIsHovered] = useState(false);
  const [isPointerInsideMenu, setIsPointerInsideMenu] = useState(false);
  const [preventHoverUntilLeave, setPreventHoverUntilLeave] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = useCallback(() => {
    setIsPointerInsideMenu(true);
    if (preventHoverUntilLeave) {
      return;
    }
    hoverTimerRef.current = setTimeout(() => {
      setIsHovered(true);
    }, 120);
  }, [preventHoverUntilLeave]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setIsHovered(false);
    setIsPointerInsideMenu(false);
    setPreventHoverUntilLeave(false);
  }, []);

  const handleTogglePinned = useCallback(() => {
    if (isPinned && isPointerInsideMenu) {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      setIsHovered(false);
      setPreventHoverUntilLeave(true);
    }

    setPinned(!isPinned);
  }, [isPinned, isPointerInsideMenu, setPinned]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      MENU_MODAL_OFFSET_PROPERTY,
      isPinned || isHovered ? MENU_EXPANDED_WIDTH : MENU_COLLAPSED_WIDTH
    );
  }, [isHovered, isPinned]);

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
      document.documentElement.style.removeProperty(MENU_MODAL_OFFSET_PROPERTY);
    };
  }, []);

  return (
    <MenuPinnedContext.Provider
      value={{ isPinned, isHovered, togglePinned: handleTogglePinned }}
    >
      <SubMenuProvider>
        <MenuWrapper
          data-testid="main-menu-wrapper"
          className={cn(
            props.className,
            isPinned && "pinned",
            isHovered && "hover-open"
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <MenuContainer>
            <MenuHeader onCreatePost={handleCreatePost} />

            <div style={{ marginBottom: scrollingGap }}>
              <div className="menu-items mt-2 flex w-full flex-col gap-1 rounded-lg bg-transparent p-0">
                <HomeMenuItem
                  to="/dashboard"
                  id="navigation-home"
                  icon={<HomeIcon />}
                  checkActiveFunc={(path) =>
                    path === "/dashboard" || path === "/dashboard/"
                  }
                  data-id="home-icon"
                >
                  Home
                </HomeMenuItem>
                {/*
                  SKE-4681. PINNED means this non-scrolling block, beside Home.
                  Inside `GroupSectionContainer` below it would scroll away,
                  which is the opposite of what pinned was asked for. The label
                  escalates with the register; the position never moves.
                */}
                <InstagramCampaignMenuEntry
                  render={({ to, label, isUrgent }) => (
                    <HomeMenuItem
                      to={to}
                      id="navigation-instagram-campaign"
                      icon={
                        <i
                          aria-hidden="true"
                          className={cn(
                            "fa-regular fa-triangle-exclamation",
                            isUrgent && "text-red-700"
                          )}
                        />
                      }
                      /*
                       * The label colour has to be set as a DESCENDANT rule.
                       * `MenuItem`'s base carries `[&_.menu-label]:text-[#676571]`,
                       * and `important: true` in the Tailwind config puts
                       * `!important` on both, so the contest is purely
                       * specificity and the 0,2,0 descendant rule beats a plain
                       * `text-red-*` anywhere. Matching the variant lets
                       * tailwind-merge drop the grey instead.
                       *
                       * red-700, not red-600: the sidebar ground is #F2F1F9,
                       * where red-600 measures 4.45:1 and misses AA by a
                       * hair. red-700 is 6.87:1.
                       */
                      className={cn(isUrgent && "[&_.menu-label]:text-red-700")}
                      data-id="instagram-campaign-icon"
                      data-urgent={isUrgent ? "true" : "false"}
                    >
                      {label}
                    </HomeMenuItem>
                  )}
                />
              </div>
            </div>
            <GroupSectionContainer
              id="grouped-menu"
              ref={scrollContainerRef}
              onScroll={onContainerScroll}
            >
              <MenuSection
                id="grouped-menu-plan-and-collobrate"
                title="Plan & Collaborate"
                style={{ marginTop: -scrollingGap }}
              >
                <BrandKitsMenuItems />
                <MenuItem
                  to="/dashboard/idea-planner"
                  id="navigation-idea-planner"
                  icon={<IdeaPlannerIcon data-id="workshop-icon" />}
                >
                  Ideas
                </MenuItem>

                <MenuItem
                  to="/dashboard/grid-preview"
                  id="navigation-planner"
                  icon={<PlannerIcon />}
                >
                  Grid Preview
                </MenuItem>
                <MenuItem
                  to="/dashboard/template-manager"
                  id="navigation-template-manager"
                  icon={<TemplatesIcon />}
                >
                  Templates
                </MenuItem>
              </MenuSection>

              <MenuSection id="grouped-menu-publish" title="Publish">
                {!isBulkHidden && (
                  <MenuItem
                    onClick={handleCreatePost}
                    id="navigation-bulk"
                    icon={<BulkUploadIcon data-id="bulk-upload-icon" />}
                  >
                    Bulk Create Posts
                  </MenuItem>
                )}
                <MenuItem
                  to="/dashboard/calendar"
                  id="navigation-calendar"
                  icon={<CalendarIcon />}
                >
                  Calendar
                </MenuItem>
                <PostsMenuItems />
                <ApprovalsMenuItems
                  user={user}
                  upsellApproval={upsellApproval}
                  stateLinks={stateLinks}
                  trackEvent={trackEvent}
                />
                <MenuItem
                  to="/dashboard/library"
                  id="navigation-library"
                  icon={<LibIcon />}
                >
                  Library
                </MenuItem>
              </MenuSection>

              <MenuSection
                id="grouped-menu-engage-and-convert"
                title="Engage & Convert"
              >
                <InboxV2MenuItems />
                <MenuItem
                  to="/sked-link-sso"
                  target="_blank"
                  icon={<SkedlinkIcon data-id="link-in-bio-icon" />}
                  id="navigation-sked-link"
                >
                  Link in bio
                </MenuItem>
              </MenuSection>

              <MenuSection id="grouped-menu-optimize" title="Optimize">
                <SocialListeningV2MenuItemsWithUpsell
                  previewActive={insightsRollout.previewActive}
                />
                <InsightsV2MenuItems
                  scrollContainerRef={scrollContainerRef}
                  user={user}
                  trackEvent={trackEvent}
                  rollout={insightsRollout}
                />
                <ReportBuilderV2MenuItem
                  upsellReportBuilder={upsellReportBuilder}
                  reportBuilderUrl={reportBuilderUrl}
                  user={user}
                  trackEvent={trackEvent}
                />
              </MenuSection>
            </GroupSectionContainer>
            <MenuFooterToggle
              isPinned={isPinned}
              togglePinned={handleTogglePinned}
            />
          </MenuContainer>
        </MenuWrapper>
      </SubMenuProvider>
    </MenuPinnedContext.Provider>
  );
}

export const Menu = MenuComponent;

const groupSectionContainerClassName =
  "menu-group-sections overflow-x-hidden overflow-y-auto pb-12 [&::-webkit-scrollbar]:w-0 hover:[&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb:hover]:bg-neutral-400";

const GroupSectionContainer = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function GroupSectionContainer({ children, className, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn(groupSectionContainerClassName, className)}
      {...props}
    >
      {children}
    </div>
  );
});

interface InsightsMenuItemsProps {
  scrollContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  user?: User;
  trackEvent: ReturnType<typeof useTrackEvent>;
  rollout: InsightsPreviewRolloutState;
}

const InsightsV2MenuItems = ({
  scrollContainerRef,
  user,
  trackEvent,
  rollout,
}: InsightsMenuItemsProps) => {
  const { menuRef, toggleSubMenu, subMenuOpened, isMenuExpanded } =
    useCollapseSubMenu("insightsV2");
  useEffect(() => {
    setTimeout(() => {
      if (subMenuOpened && scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop =
          scrollContainerRef.current.scrollHeight;
      }
    }, 500);
  }, [scrollContainerRef, subMenuOpened]);

  const upsellInsightLabel = needsLabelsReportingUpsell(user);
  const isUpsellAudience =
    !!user && !slPermission.hasSocialListeningAudience(user);
  // GA4 surfaces (Website Traffic + Tag Health) are behind the pre-launch
  // domain gate — the same check the insights pages themselves use.
  const canViewGa4 = hasGa4SettingsAccess(user);

  const insightsNav = makeInsightsNavHandler;

  const socialListeningUnavailableRoute =
    slPermission.getSocialListeningUnavailableRoute(
      insightsPath("/social-listening", rollout.previewActive),
      user
    );

  const brandSummaryTarget = insightsPath(
    "/analytics/overview",
    rollout.previewActive
  );
  const audienceTarget = isUpsellAudience
    ? socialListeningUnavailableRoute
    : insightsPath("/social-listening/audience", rollout.previewActive);
  const labelsTarget = insightsPath("/labels", rollout.previewActive);
  const adsTarget = insightsPath("/ads", rollout.previewActive);
  const websiteTrafficTarget = insightsPath(
    "/website-traffic",
    rollout.previewActive
  );
  const tagHealthTarget = insightsPath("/tag-health", rollout.previewActive);

  const labelsOnClick = () => {
    if (upsellInsightLabel) {
      trackEvent({
        eventName: UPGRADE_LABELS_REPORTING,
        useServices: ["AMP", "HS"],
      });
      goToManagePlan();
    }
    trackEvent({
      eventName: "nav-label-page",
      useServices: ["AMP"],
      metadata: {
        Location: "Side Nav",
      },
    });
  };

  return (
    <>
      <ParentMenuItem
        to={insightsPath("", rollout.previewActive)}
        icon={<InsightsIcon data-id="insights-v2-icon" />}
        id="navigation-insights-v2"
        target="_blank"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleSubMenu();
        }}
        checkActiveFunc={(path) =>
          path === "/dashboard/insights/inbox-metrics" ||
          isInsightsSubpathActive(path, "/analytics") ||
          isInsightsSubpathActive(path, "/website-traffic") ||
          isInsightsSubpathActive(path, "/tag-health") ||
          isInsightsSubpathActive(path, "/labels") ||
          isInsightsSubpathActive(path, "/ads") ||
          isInsightsSubpathActive(path, "/social-listening/audience")
        }
      >
        Insights
      </ParentMenuItem>
      <SubMenuContainer
        isMenuExpanded={isMenuExpanded}
        maxHeight={canViewGa4 ? 330 : 220}
        opened={subMenuOpened}
        className="insights-v2-sub-menu-container"
        ref={menuRef}
      >
        <SubMenuItem
          icon={<span></span>}
          id="navigation-insights-v2-brand-summary"
          to={brandSummaryTarget}
          checkActiveFunc={(path) =>
            isInsightsSubpathActive(path, "/analytics")
          }
          onClick={insightsNav(brandSummaryTarget)}
        >
          Brand Summary
        </SubMenuItem>
        <SubMenuItem
          icon={<span></span>}
          id="navigation-social-listening-v2-audience"
          to={audienceTarget}
          checkActiveFunc={(path) =>
            isInsightsSubpathActive(path, "/social-listening/audience")
          }
          onClick={insightsNav(audienceTarget)}
        >
          <UpsellTextWrapped text="Audience" upsell={isUpsellAudience} />
        </SubMenuItem>
        {localStorageFeatureToggle.get(FeatureName.InboxInsights) && (
          <SubMenuItem
            icon={<span />}
            id="navigation-insights-inbox-metrics"
            to="/dashboard/insights/inbox-metrics"
          >
            Inbox Metrics
          </SubMenuItem>
        )}
        <SubMenuItem
          icon={<span></span>}
          id="navigation-insights-v2-labels"
          to={upsellInsightLabel ? undefined : labelsTarget}
          onClick={
            upsellInsightLabel
              ? labelsOnClick
              : insightsNav(labelsTarget, labelsOnClick)
          }
        >
          <UpsellTextWrapped text="Labels" upsell={upsellInsightLabel} />
        </SubMenuItem>
        <SubMenuItem
          icon={<span></span>}
          id="navigation-insights-v2-paid-ads"
          to={adsTarget}
          checkActiveFunc={(path) => isInsightsSubpathActive(path, "/ads")}
          onClick={insightsNav(adsTarget)}
        >
          <UpsellTextWrapped text="Paid Social" upsell={false} />
        </SubMenuItem>
        {canViewGa4 && (
          <SubMenuItem
            icon={<span></span>}
            id="navigation-insights-v2-website-traffic"
            to={websiteTrafficTarget}
            checkActiveFunc={(path) =>
              isInsightsSubpathActive(path, "/website-traffic")
            }
            onClick={insightsNav(websiteTrafficTarget)}
          >
            Website Traffic
          </SubMenuItem>
        )}
        {canViewGa4 && (
          <SubMenuItem
            icon={<span></span>}
            id="navigation-insights-v2-tag-health"
            to={tagHealthTarget}
            checkActiveFunc={(path) =>
              isInsightsSubpathActive(path, "/tag-health")
            }
            onClick={insightsNav(tagHealthTarget)}
          >
            Tag Health
          </SubMenuItem>
        )}
      </SubMenuContainer>
    </>
  );
};

const SocialListeningV2MenuItemsWithUpsell = ({
  previewActive,
}: {
  previewActive: boolean;
}) => {
  const user = useSelector(getUser);

  const isSocialListeningEnabled = slPermission.enabledSocialListening(user);
  const socialListeningUnavailableRoute =
    slPermission.getSocialListeningUnavailableRoute(
      insightsPath("/social-listening", previewActive),
      user
    );
  const isUpsellSentiment = !slPermission.hasSocialListeningSentiment(user);
  const isUpsellKeyword = !slPermission.hasSocialListeningKeyword(user);

  const { menuRef, toggleSubMenu, subMenuOpened, isMenuExpanded } =
    useCollapseSubMenu("socialListeningV2");

  const insightsNav = makeInsightsNavHandler;

  if (!isSocialListeningEnabled) {
    return (
      <ParentMenuItem
        to={socialListeningUnavailableRoute}
        icon={<SocialListeningIcon data-id="social-listening-v2-icon" />}
        id="navigation-social-listening-v2"
        onClick={insightsNav(socialListeningUnavailableRoute)}
      >
        <UpsellTextWrapped text="Social Listening" upsell />
      </ParentMenuItem>
    );
  }

  const summaryTarget = insightsPath(
    "/social-listening/summary",
    previewActive
  );
  const sentimentTarget = isUpsellSentiment
    ? socialListeningUnavailableRoute
    : insightsPath("/social-listening/sentiment", previewActive);
  const competitorsTarget = insightsPath(
    "/social-listening/competitors",
    previewActive
  );
  const keywordTarget = isUpsellKeyword
    ? socialListeningUnavailableRoute
    : insightsPath("/social-listening/keyword", previewActive);

  return (
    <>
      <ParentMenuItem
        icon={<SocialListeningIcon data-id="social-listening-v2-icon" />}
        id="navigation-social-listening-v2"
        checkActiveFunc={(path) =>
          isInsightsSubpathActive(path, "/social-listening") &&
          !isInsightsSubpathActive(path, "/social-listening/audience")
        }
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleSubMenu();
        }}
      >
        Social Listening
      </ParentMenuItem>
      <SubMenuContainer
        ref={menuRef}
        isMenuExpanded={isMenuExpanded}
        maxHeight={220}
        opened={subMenuOpened}
        className="social-listening-v2-sub-menu-container"
      >
        <SubMenuItem
          icon={<span></span>}
          id="navigation-social-listening-v2-summary"
          to={summaryTarget}
          checkActiveFunc={(path) =>
            isInsightsSubpathActive(path, "/social-listening/summary")
          }
          onClick={insightsNav(summaryTarget)}
        >
          Summary
        </SubMenuItem>
        <SubMenuItem
          icon={<span></span>}
          id="navigation-social-listening-v2-sentiment"
          to={sentimentTarget}
          checkActiveFunc={(path) =>
            isInsightsSubpathActive(path, "/social-listening/sentiment")
          }
          onClick={insightsNav(sentimentTarget)}
        >
          <UpsellTextWrapped text="Sentiment" upsell={isUpsellSentiment} />
        </SubMenuItem>
        <SubMenuItem
          icon={<span></span>}
          id="navigation-social-listening-v2-competitors"
          to={competitorsTarget}
          checkActiveFunc={(path) =>
            isInsightsSubpathActive(path, "/social-listening/competitors")
          }
          onClick={insightsNav(competitorsTarget)}
        >
          Competitors
        </SubMenuItem>
        <SubMenuItem
          icon={<span></span>}
          id="navigation-social-listening-v2-keyword"
          to={keywordTarget}
          checkActiveFunc={(path) =>
            isInsightsSubpathActive(path, "/social-listening/keyword")
          }
          onClick={insightsNav(keywordTarget)}
        >
          <UpsellTextWrapped text="Keywords" upsell={isUpsellKeyword} />
        </SubMenuItem>
      </SubMenuContainer>
    </>
  );
};

function InboxV2MenuItems() {
  const { menuRef, openSubMenu, subMenuOpened, isMenuExpanded } =
    useCollapseSubMenu("inboxV2");
  const [pinnedBoards, setPinnedBoards] = useState<
    Array<{ id: string; name: string; pinned: boolean }>
  >(() => getPinnedInboxV2Boards());
  const pinnedBoardIds = useMemo(
    () => new Set(pinnedBoards.map((board) => board.id)),
    [pinnedBoards]
  );
  const refreshPinnedBoards = useCallback((forceNoCache = false) => {
    let cancelled = false;

    realInboxV2DataSource
      .getBoards({ forceNoCache })
      .then((payload) => {
        if (cancelled) {
          return;
        }

        setPinnedBoards(
          payload.boards.filter((board) => Boolean(board.pinned))
        );
      })
      .catch(() => {
        if (!cancelled) {
          setPinnedBoards([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return refreshPinnedBoards();
  }, [refreshPinnedBoards]);

  useEffect(
    () =>
      subscribeInboxV2BoardsChanged(() => {
        refreshPinnedBoards(true);
      }),
    [refreshPinnedBoards]
  );

  return (
    <>
      <ParentMenuItem
        icon={<InboxIcon />}
        id="navigation-inbox-v2"
        to={INBOX_V2_BOARD_ROOT_PATH}
        checkActiveFunc={(path) => path.includes("/dashboard/inbox-v2")}
        onClick={() => openSubMenu()}
      >
        Inbox
      </ParentMenuItem>
      <SubMenuContainer
        ref={menuRef}
        isMenuExpanded={isMenuExpanded}
        maxHeight={128 + pinnedBoards.length * 32}
        opened={subMenuOpened}
        className="inbox-v2-sub-menu-container"
      >
        <SubMenuItem
          icon={<span />}
          id="navigation-inbox-v2-board"
          to={INBOX_V2_BOARD_ROOT_PATH}
          checkActiveFunc={(path) =>
            isInboxV2BoardSubmenuPathActive(path, pinnedBoardIds)
          }
        >
          Board
        </SubMenuItem>
        {pinnedBoards.map((board) => (
          <SubMenuItem
            icon={<span />}
            id={`navigation-inbox-v2-board-${board.id}`}
            key={board.id}
            to={getInboxV2BoardPath(board.id)}
            checkActiveFunc={(path) =>
              isInboxV2PinnedBoardPathActive(path, board.id)
            }
          >
            {board.name}
          </SubMenuItem>
        ))}
        <SubMenuItem
          icon={<span />}
          id="navigation-inbox-v2-reply-templates"
          to="/dashboard/inbox-v2/reply-templates"
          checkActiveFunc={(path) =>
            path.includes("/dashboard/inbox-v2/reply-templates")
          }
        >
          Reply Templates
        </SubMenuItem>
        <SubMenuItem
          icon={<span />}
          id="navigation-inbox-v2-tags"
          to="/dashboard/inbox-v2/tags"
          checkActiveFunc={(path) => path.includes("/dashboard/inbox-v2/tags")}
        >
          Tags
        </SubMenuItem>
      </SubMenuContainer>
    </>
  );
}

function getPinnedInboxV2Boards() {
  return (
    readInboxV2ApiCache<InboxV2BoardsPayload>(
      INBOX_V2_GET_BOARDS_CACHE_KEY
    )?.boards.filter((board) => Boolean(board.pinned)) ?? []
  );
}

const BrandKitsMenuItems = () => {
  const { menuRef, toggleSubMenu, subMenuOpened, isMenuExpanded } =
    useCollapseSubMenu("brandKits");

  return (
    <>
      <ParentMenuItem
        icon={<BrandKitsIcon data-id="brand-kits-icon" />}
        id="navigation-brand-kits"
        checkActiveFunc={(path) => {
          return (
            path.includes("/dashboard/brand-kits") ||
            path.includes("/dashboard/workshop/labels")
          );
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleSubMenu();
        }}
      >
        Brand Kit
      </ParentMenuItem>
      <SubMenuContainer
        ref={menuRef}
        isMenuExpanded={isMenuExpanded}
        maxHeight={180}
        opened={subMenuOpened}
        className="brand-kits-sub-menu-container"
      >
        <SubMenuItem
          icon={<span></span>}
          id="navigation-brand-kits-description"
          to="/dashboard/brand-kits/brand-description"
          checkActiveFunc={(path) => {
            return path.includes("/brand-kits/brand-description");
          }}
        >
          Brand Description
        </SubMenuItem>
        <SubMenuItem
          icon={<span></span>}
          id="navigation-brand-kits-tov"
          to="/dashboard/brand-kits/tone-of-voice"
          checkActiveFunc={(path) => {
            return path.includes("/brand-kits/tone-of-voice");
          }}
        >
          Tone of Voice
        </SubMenuItem>
        <SubMenuItem
          icon={<span></span>}
          id="navigation-brand-kits-instructions"
          to="/dashboard/brand-kits/instructions"
          checkActiveFunc={(path) => {
            return path.includes("/brand-kits/instructions");
          }}
        >
          Instructions
        </SubMenuItem>
        <SubMenuItem
          icon={<span></span>}
          id="navigation-brand-kits-labels"
          to="/dashboard/workshop/labels"
          checkActiveFunc={(path) => {
            return path.includes("/dashboard/workshop/labels");
          }}
        >
          Labels
        </SubMenuItem>
      </SubMenuContainer>
    </>
  );
};

const ApprovalsMenuItems = ({
  user,
  upsellApproval,
  stateLinks,
  trackEvent,
}: {
  user: User;
  upsellApproval: boolean;
  stateLinks: { length: number };
  trackEvent: ReturnType<typeof useTrackEvent>;
}) => {
  const { menuRef, openSubMenu, subMenuOpened, isMenuExpanded } =
    useCollapseSubMenu("approvals");
  const canAccessExternalApprovals =
    hasExternalApprovals(user) && !user.preferences?.hideReviewLink;

  if (user.preferences?.hideReviewLink && !hasApprovals(user)) {
    return null;
  }

  if (hasApprovals(user)) {
    return (
      <>
        <ParentMenuItem
          icon={<ExternalPreviewIcon />}
          id="navigation-approvals"
          to="/dashboard/approvals/board"
          checkActiveFunc={(path) => path.includes("/dashboard/approvals")}
          onClick={() => openSubMenu()}
        >
          Approvals
        </ParentMenuItem>
        <SubMenuContainer
          ref={menuRef}
          isMenuExpanded={isMenuExpanded}
          maxHeight={canAccessExternalApprovals ? 96 : 48}
          opened={subMenuOpened}
          className="approvals-sub-menu-container"
        >
          <SubMenuItem
            icon={<span></span>}
            id="navigation-approvals-board"
            to="/dashboard/approvals/board"
            checkActiveFunc={(path) =>
              path.includes("/dashboard/approvals/board")
            }
          >
            Board
          </SubMenuItem>
          {canAccessExternalApprovals && (
            <SubMenuItem
              icon={<span></span>}
              id="navigation-approvals-links-themes"
              to="/dashboard/approvals/links-themes"
              checkActiveFunc={(path) =>
                path.includes("/dashboard/approvals/links-themes")
              }
            >
              Links &amp; themes
            </SubMenuItem>
          )}
        </SubMenuContainer>
      </>
    );
  }

  return (
    <ApprovalTooltipWrapper show={!hasApprovals(user) && !upsellApproval}>
      <MenuItem
        data-testid="approvalsOpenModalButton"
        to="/dashboard/approvals"
        onClick={() => {
          if (upsellApproval) {
            trackEvent({
              eventName: UPGRADE_APPROVAL_VIEWED,
              useServices: ["AMP", "HS"],
            });
            goToManagePlan();
          } else if (!hasApprovals(user) && stateLinks.length === 0) {
            openSupportForEnableApprovals();
          }
        }}
        icon={<ExternalPreviewIcon />}
        id="navigation-approvals"
      >
        <UpsellTextWrapped text="Approvals" upsell={upsellApproval} />
      </MenuItem>
    </ApprovalTooltipWrapper>
  );
};

const PostsMenuItems = () => {
  const { menuRef, openSubMenu, subMenuOpened, isMenuExpanded } =
    useCollapseSubMenu("posts");
  const location = useLocation();
  const postsView =
    getPostsViewFromPath(location.pathname) ?? getStoredPostsView();

  return (
    <>
      <ParentMenuItem
        icon={
          <i
            aria-hidden="true"
            className="menu-font-awesome-icon fa-regular fa-newspaper h-[18px] w-[18px] text-center text-[17px] leading-[18px]"
          />
        }
        id="navigation-posts"
        to={buildPostsRoute(PAGES.UPCOMING, postsView)}
        checkActiveFunc={(path) =>
          path.includes("/dashboard/schedules/list/") ||
          path.includes("/dashboard/schedules/table/")
        }
        onClick={() => openSubMenu()}
      >
        Posts
      </ParentMenuItem>
      <SubMenuContainer
        ref={menuRef}
        isMenuExpanded={isMenuExpanded}
        maxHeight={124}
        opened={subMenuOpened}
        className="posts-sub-menu-container"
      >
        <SubMenuItem
          icon={<span />}
          id="navigation-drafts"
          to={buildPostsRoute(PAGES.DRAFT, postsView)}
          checkActiveFunc={(path) => path.endsWith("/drafts")}
        >
          Drafts
        </SubMenuItem>
        <SubMenuItem
          icon={<span />}
          id="navigation-upcoming"
          to={buildPostsRoute(PAGES.UPCOMING, postsView)}
          checkActiveFunc={(path) => path.endsWith("/active")}
        >
          Upcoming
        </SubMenuItem>
        <SubMenuItem
          icon={<span />}
          id="navigation-archives"
          to={buildPostsRoute(PAGES.ARCHIVE, postsView)}
          checkActiveFunc={(path) => path.endsWith("/inactive")}
        >
          Archive
        </SubMenuItem>
      </SubMenuContainer>
    </>
  );
};

const scrollingGap = 10;

// localStorage key for pinned menu state
const MENU_PINNED_KEY = "sked-menu-pinned";
const MENU_MODAL_OFFSET_PROPERTY = "--menu-modal-offset";
const MENU_COLLAPSED_WIDTH = "52px";
const MENU_EXPANDED_WIDTH = "200px";

const getDefaultPinnedState = () => window.innerWidth >= 1900;

// Hook to manage pinned menu state with localStorage persistence
const useMenuPinned = () => {
  const [hasStoredPreference, setHasStoredPreference] = useState(() => {
    try {
      return localStorage.getItem(MENU_PINNED_KEY) !== null;
    } catch {
      return false;
    }
  });

  const [isPinned, setIsPinned] = useState(() => {
    try {
      const storedPreference = localStorage.getItem(MENU_PINNED_KEY);
      if (storedPreference !== null) {
        return storedPreference === "true";
      }
    } catch {
      // localStorage not available
    }

    return getDefaultPinnedState();
  });

  useEffect(() => {
    if (hasStoredPreference) {
      return;
    }

    const handleResize = () => {
      setIsPinned(getDefaultPinnedState());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [hasStoredPreference]);

  const setPinned = useCallback((nextPinned: boolean) => {
    setHasStoredPreference(true);
    setIsPinned(nextPinned);
    try {
      localStorage.setItem(MENU_PINNED_KEY, String(nextPinned));
    } catch {
      // localStorage not available
    }
  }, []);

  return { isPinned, setPinned };
};

const useIsMenuCollapsed = (isPinned: boolean, isHovered: boolean) => {
  return !isHovered && !isPinned;
};

// Extract text content for tooltip (outside component for performance)
const getTooltipText = (node: React.ReactNode): string => {
  if (typeof node === "string") return node;
  if (React.isValidElement(node)) {
    const childContent = node.props?.children;
    if (typeof childContent === "string") return childContent;
    if (node.props?.text) return node.props.text;
  }
  return "";
};

const MenuItemLink = ({
  icon,
  children,
  iconWrapperClassName,
  labelClassName,
  ...props
}: MenuItemLinkProps) => {
  const dispatch = useDispatch();
  const { isPinned, isHovered } = React.useContext(MenuPinnedContext);
  const showTooltip = useIsMenuCollapsed(isPinned, isHovered);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    dispatch({ type: COMMON_CLEAN_UP_LOADING });
    props.onClick?.(e);
  };

  const tooltipText = getTooltipText(children);

  const linkContent = (
    <Link {...props} onClick={handleClick}>
      {icon && (
        <span
          className={cn(
            "menu-icon flex shrink-0 items-center justify-center",
            iconWrapperClassName
          )}
        >
          {icon}
        </span>
      )}
      <Name className={labelClassName}>{children}</Name>
    </Link>
  );

  if (showTooltip && tooltipText) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
        <TooltipContent side="right">{tooltipText}</TooltipContent>
      </Tooltip>
    );
  }

  return linkContent;
};

const UpsellTextWrapped = ({
  text,
  upsell,
}: {
  text: string;
  upsell: boolean;
}) => {
  return upsell ? (
    <InsightLabelWrap>
      {text}
      <span className="menu-upsell-badge ml-3 flex h-5 w-5 items-center justify-center rounded-[15px] bg-[#4F31FF] text-xs font-semibold leading-[25px] text-black">
        <i
          className="fa-solid fa-rocket menu-upsell-icon text-white"
          aria-hidden="true"
        />
      </span>
    </InsightLabelWrap>
  ) : (
    <>{text}</>
  );
};

function MenuContainer({
  children,
  className,
}: React.HTMLAttributes<HTMLElement>) {
  return (
    <nav
      className={cn(
        "menu-container fixed z-[1040] flex h-screen w-[var(--menu-width)] flex-col items-stretch bg-[#f2f1f9] p-2 select-none transition-[width,box-shadow] duration-[var(--menu-transition-duration)] ease-[var(--menu-transition-ease)] motion-reduce:transition-none",
        className
      )}
    >
      {children}
    </nav>
  );
}

function CreatePostButton({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "menu-create-post-button relative flex h-9 w-full shrink-0 cursor-pointer items-center justify-start gap-3 whitespace-nowrap rounded-lg bg-[#ED207C] px-3 py-2 shadow-[0_1px_2px_rgba(16,24,40,0.05)] transition-[width,padding,gap,filter,box-shadow] duration-[var(--menu-transition-duration)] ease-[var(--menu-transition-ease)] hover:brightness-105 hover:shadow-[0_2px_4px_rgba(16,24,40,0.08)] motion-reduce:transition-none [&_svg]:max-h-[18px] [&_svg]:max-w-[18px] [&_svg]:shrink-0 [&_svg]:overflow-visible",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function InsightLabelWrap({ children }: { children: React.ReactNode }) {
  return (
    <span className="menu-upsell-label flex items-center">{children}</span>
  );
}

function SkedSocial({ children }: { children: React.ReactNode }) {
  return (
    <div className="menu-brand mb-2 flex min-h-9 w-full items-center gap-2 p-0 [&>svg]:h-9 [&>svg]:min-w-9 [&>svg]:max-w-9">
      {children}
    </div>
  );
}

function ToggleIcon({ isPinned }: { isPinned: boolean }) {
  return (
    <span className="menu-toggle-icon pointer-events-none flex items-center justify-center text-[#B3B3B3] transition-colors duration-150 ease-in-out">
      <i
        aria-hidden="true"
        className={cn(
          "fa-sidebar text-base leading-none",
          isPinned ? "fa-solid" : "fa-regular"
        )}
      />
    </span>
  );
}

const MenuToggleButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(function MenuToggleButton(props, ref) {
  // eslint-disable-next-line react/prop-types
  const { children, className, ...buttonProps } = props;

  return (
    <button
      ref={ref}
      className={cn(
        "menu-footer-toggle absolute bottom-2 right-2 z-10 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-transparent p-2 transition-colors duration-150 ease-in-out hover:bg-[#FCFAFF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4F31FF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F2F1F9] hover:[&_.menu-toggle-icon]:text-[#4F31FF]",
        className
      )}
      {...buttonProps}
    >
      {children}
    </button>
  );
});

function MenuItem({
  className,
  ...props
}: MenuItemLinkProps & { className?: string }) {
  return (
    <MenuItemLink
      className={cn(
        "menu-item relative flex h-7 w-full shrink-0 items-center gap-2 rounded-[4px] bg-transparent px-3 py-1 text-[#676571] no-underline transition-[width,padding,color,background-color,border-radius,box-shadow] duration-[var(--menu-transition-duration)] ease-[var(--menu-transition-ease)] [text-decoration:none] focus:[text-decoration:none] active:[text-decoration:none] visited:[text-decoration:none] motion-reduce:transition-none [&_.menu-label]:text-[#676571] [&_.menu-icon_svg]:max-h-[18px] [&_.menu-icon_svg]:max-w-[18px] [&_.menu-icon_svg]:overflow-visible [&_.menu-icon_svg]:text-[#676571] [&_.menu-icon_svg]:transition-colors [&_.menu-icon_svg_path]:transition-all [&_.menu-font-awesome-icon]:text-[#676571] [&_[data-id='labels-icon']]:h-[18px] [&_[data-id='labels-icon']]:w-[18px] [&.active]:bg-white [&.active]:text-[#1a1823] [&.active]:shadow-[0_1px_2px_rgba(16,24,40,0.05)] [&.active_.menu-label]:text-[#1a1823] [&.active_.menu-icon_svg]:text-[#4F31FF] [&.active_.menu-font-awesome-icon]:text-[#4F31FF] [&.active_.menu-upsell-badge]:bg-white [&.active_.menu-upsell-icon]:text-[#4F31FF] max-[667px]:h-8 max-[667px]:[&_.menu-icon_svg]:max-h-4 max-[667px]:[&_.menu-icon_svg]:max-w-4",
        className
      )}
      {...props}
    />
  );
}

/*
 * `className` is MERGED rather than overwritten. It used to sit after the
 * spread, which silently discarded any class a caller passed: the campaign
 * entry's week 3 red escalation set one and nothing happened. `Home` passes
 * none, so nothing about it changes.
 */
function HomeMenuItem({
  className,
  ...props
}: MenuItemLinkProps & { className?: string }) {
  return (
    <MenuItem
      {...props}
      className={cn("menu-home-item rounded-lg", className)}
    />
  );
}

function SubMenuItem(props: MenuItemLinkProps) {
  return (
    <MenuItem
      {...props}
      className="menu-sub-item mr-0 gap-[18px] rounded-lg px-2 pl-[22px] before:pointer-events-none before:absolute before:bottom-0 before:left-[22px] before:top-0 before:w-px before:bg-neutral-200 before:content-[''] [&_.menu-label]:text-sm [&_.menu-label]:leading-5 [&_.menu-icon_svg]:max-h-4 [&_.menu-icon_svg]:max-w-4"
    />
  );
}

function ParentMenuItem(props: MenuItemLinkProps) {
  return <MenuItem {...props} className="menu-parent-item" />;
}

const menuWrapperBaseClasses = [
  "menu-wrapper",
  "relative",
  "z-[1040]",
  "h-screen",
  "w-[var(--menu-width)]",
  "shrink-0",
  "[--menu-width:52px]",
  "[--menu-width-max:200px]",
  "[--menu-transition-duration:200ms]",
  "[--menu-transition-ease:cubic-bezier(0.22,1,0.36,1)]",
  "transition-[width]",
  "duration-[var(--menu-transition-duration)]",
  "ease-[var(--menu-transition-ease)]",
  "motion-reduce:transition-none",
  "[&:hover_.scroll-more-hint]:block",
  "[&_.scroll-more-hint]:hidden",
  "[&_.insights-sub-menu-container]:invisible",
  "[&_.insights-v2-sub-menu-container]:invisible",
  "[&_.social-listening-sub-menu-container]:invisible",
  "[&_.social-listening-v2-sub-menu-container]:invisible",
  "[&_.approvals-sub-menu-container]:invisible",
  "[&_.posts-sub-menu-container]:invisible",
  "[&_.inbox-v2-sub-menu-container]:invisible",
  "[&.hover-open_.insights-sub-menu-container]:visible",
  "[&.hover-open_.insights-v2-sub-menu-container]:visible",
  "[&.hover-open_.social-listening-sub-menu-container]:visible",
  "[&.hover-open_.social-listening-v2-sub-menu-container]:visible",
  "[&.hover-open_.approvals-sub-menu-container]:visible",
  "[&.hover-open_.posts-sub-menu-container]:visible",
  "[&.hover-open_.inbox-v2-sub-menu-container]:visible",
  "[&.pinned_.insights-sub-menu-container]:visible",
  "[&.pinned_.insights-v2-sub-menu-container]:visible",
  "[&.pinned_.social-listening-sub-menu-container]:visible",
  "[&.pinned_.social-listening-v2-sub-menu-container]:visible",
  "[&.pinned_.approvals-sub-menu-container]:visible",
  "[&.pinned_.posts-sub-menu-container]:visible",
  "[&.pinned_.inbox-v2-sub-menu-container]:visible",
];

const menuWrapperCollapsedClasses = [
  "[&_.menu-container]:items-center",
  "[&_.menu-container]:overflow-hidden",
  "[&_.menu-container]:p-2",
  "[&_.menu-label]:block",
  "[&_.menu-label]:max-w-0",
  "[&_.menu-label]:translate-x-[-4px]",
  "[&_.menu-label]:overflow-hidden",
  "[&_.menu-label]:opacity-0",
  "[&_.menu-label]:transition-[max-width,opacity,transform,color]",
  "[&_.menu-label]:duration-[var(--menu-transition-duration)]",
  "[&_.menu-label]:ease-[var(--menu-transition-ease)]",
  "motion-reduce:[&_.menu-label]:transition-none",
  "[&_.menu-items]:w-auto",
  "[&_.menu-items]:items-center",
  "[&_.menu-item]:flex",
  "[&_.menu-item]:h-7",
  "[&_.menu-item]:w-7",
  "[&_.menu-item]:items-center",
  "[&_.menu-item]:justify-center",
  "[&_.menu-item]:gap-0",
  "[&_.menu-item]:rounded-[20px]",
  "[&_.menu-item]:p-1",
  "[&_.menu-item_svg]:m-0",
  "[&_.menu-item_svg]:shrink-0",
  "[&_.menu-item.active]:h-7",
  "[&_.menu-item.active]:w-7",
  "[&_.menu-item.active]:rounded-lg",
  "[&_.menu-item.active]:p-1",
  "[&_.menu-create-post-button]:h-9",
  "[&_.menu-create-post-button]:w-9",
  "[&_.menu-create-post-button]:justify-center",
  "[&_.menu-create-post-button]:gap-0",
  "[&_.menu-create-post-button]:p-2",
  "[&_.menu-create-post-button_svg]:static",
  "[&_.menu-group-heading]:invisible",
  "[&_.menu-container]:after:pointer-events-none",
  "[&_.menu-container]:after:absolute",
  "[&_.menu-container]:after:top-0",
  "[&_.menu-container]:after:right-0",
  "[&_.menu-container]:after:bottom-0",
  "[&_.menu-container]:after:w-px",
  "[&_.menu-container]:after:bg-[linear-gradient(to_right,rgba(0,0,0,0.04),transparent)]",
  "[&_.menu-container]:after:shadow-[1px_0_4px_rgba(0,0,0,0.03)]",
  "[&_.menu-container]:after:content-['']",
  "[&_.menu-brand]:w-9",
  "[&_.menu-brand]:justify-center",
  "[&_.menu-brand]:gap-0",
  "[&_.menu-brand]:p-0",
];

const menuWrapperExpandedClasses = [
  "[&.hover-open]:w-[var(--menu-width-max)]",
  "[&.forced-open]:w-[var(--menu-width-max)]",
  "[&.pinned]:w-[var(--menu-width-max)]",
  "[&.hover-open_.menu-container]:overflow-visible",
  "[&.forced-open_.menu-container]:overflow-visible",
  "[&.pinned_.menu-container]:overflow-visible",
  "[&.hover-open_.menu-mini-divider]:hidden",
  "[&.forced-open_.menu-mini-divider]:hidden",
  "[&.pinned_.menu-mini-divider]:hidden",
  "[&.hover-open_.menu-label]:max-w-[150px]",
  "[&.forced-open_.menu-label]:max-w-[150px]",
  "[&.pinned_.menu-label]:max-w-[150px]",
  "[&.hover-open_.menu-label]:translate-x-0",
  "[&.forced-open_.menu-label]:translate-x-0",
  "[&.pinned_.menu-label]:translate-x-0",
  "[&.hover-open_.menu-label]:opacity-100",
  "[&.forced-open_.menu-label]:opacity-100",
  "[&.pinned_.menu-label]:opacity-100",
  "[&.hover-open_.menu-group-heading]:visible",
  "[&.forced-open_.menu-group-heading]:visible",
  "[&.pinned_.menu-group-heading]:visible",
  "[&.hover-open_.menu-items]:w-full",
  "[&.forced-open_.menu-items]:w-full",
  "[&.pinned_.menu-items]:w-full",
  "[&.hover-open_.menu-items]:items-stretch",
  "[&.forced-open_.menu-items]:items-stretch",
  "[&.pinned_.menu-items]:items-stretch",
  "[&.hover-open_.menu-brand]:w-full",
  "[&.forced-open_.menu-brand]:w-full",
  "[&.pinned_.menu-brand]:w-full",
  "[&.hover-open_.menu-brand]:justify-start",
  "[&.forced-open_.menu-brand]:justify-start",
  "[&.pinned_.menu-brand]:justify-start",
  "[&.hover-open_.menu-brand]:gap-2",
  "[&.forced-open_.menu-brand]:gap-2",
  "[&.pinned_.menu-brand]:gap-2",
  "[&.hover-open_.menu-item]:h-7",
  "[&.forced-open_.menu-item]:h-7",
  "[&.pinned_.menu-item]:h-7",
  "[&.hover-open_.menu-item]:w-full",
  "[&.forced-open_.menu-item]:w-full",
  "[&.pinned_.menu-item]:w-full",
  "[&.hover-open_.menu-item]:justify-start",
  "[&.forced-open_.menu-item]:justify-start",
  "[&.pinned_.menu-item]:justify-start",
  "[&.hover-open_.menu-item]:gap-2",
  "[&.forced-open_.menu-item]:gap-2",
  "[&.pinned_.menu-item]:gap-2",
  "[&.hover-open_.menu-item]:rounded-[4px]",
  "[&.forced-open_.menu-item]:rounded-[4px]",
  "[&.pinned_.menu-item]:rounded-[4px]",
  "[&.hover-open_.menu-item]:px-3",
  "[&.forced-open_.menu-item]:px-3",
  "[&.pinned_.menu-item]:px-3",
  "[&.hover-open_.menu-item]:py-1",
  "[&.forced-open_.menu-item]:py-1",
  "[&.pinned_.menu-item]:py-1",
  "[&.hover-open_.menu-sub-item]:px-2",
  "[&.forced-open_.menu-sub-item]:px-2",
  "[&.pinned_.menu-sub-item]:px-2",
  "[&.hover-open_.menu-sub-item]:pl-[22px]",
  "[&.forced-open_.menu-sub-item]:pl-[22px]",
  "[&.pinned_.menu-sub-item]:pl-[22px]",
  "[&.hover-open_.menu-sub-item.active]:w-[calc(var(--menu-width-max)-20px)]",
  "[&.forced-open_.menu-sub-item.active]:w-[calc(var(--menu-width-max)-20px)]",
  "[&.pinned_.menu-sub-item.active]:w-[calc(var(--menu-width-max)-20px)]",
  "[&.hover-open_.menu-sub-item.active]:rounded-lg",
  "[&.forced-open_.menu-sub-item.active]:rounded-lg",
  "[&.pinned_.menu-sub-item.active]:rounded-lg",
  "[&.hover-open_.menu-create-post-button]:h-9",
  "[&.forced-open_.menu-create-post-button]:h-9",
  "[&.pinned_.menu-create-post-button]:h-9",
  "[&.hover-open_.menu-create-post-button]:w-full",
  "[&.forced-open_.menu-create-post-button]:w-full",
  "[&.pinned_.menu-create-post-button]:w-full",
  "[&.hover-open_.menu-create-post-button]:justify-start",
  "[&.forced-open_.menu-create-post-button]:justify-start",
  "[&.pinned_.menu-create-post-button]:justify-start",
  "[&.hover-open_.menu-create-post-button]:gap-3",
  "[&.forced-open_.menu-create-post-button]:gap-3",
  "[&.pinned_.menu-create-post-button]:gap-3",
  "[&.hover-open_.menu-create-post-button]:px-3",
  "[&.forced-open_.menu-create-post-button]:px-3",
  "[&.pinned_.menu-create-post-button]:px-3",
  "[&.hover-open_.menu-container]:w-[var(--menu-width-max)]",
  "[&.forced-open_.menu-container]:w-[var(--menu-width-max)]",
  "[&.pinned_.menu-container]:w-[var(--menu-width-max)]",
  "[&.hover-open_.menu-container]:items-stretch",
  "[&.forced-open_.menu-container]:items-stretch",
  "[&.pinned_.menu-container]:items-stretch",
  "[&.hover-open_.menu-container]:after:hidden",
  "[&.forced-open_.menu-container]:after:hidden",
  "[&.pinned_.menu-container]:after:hidden",
  "[&.hover-open_.menu-container]:shadow-none",
  "[&.forced-open_.menu-container]:shadow-none",
  "[&.pinned_.menu-container]:shadow-none",
  "[&.hover-open_.menu-item:hover]:w-[calc(var(--menu-width-max)-20px)]",
  "[&.forced-open_.menu-item:hover]:w-[calc(var(--menu-width-max)-20px)]",
  "[&.pinned_.menu-item:hover]:w-[calc(var(--menu-width-max)-20px)]",
  "[&.hover-open_.menu-item:hover]:rounded-lg",
  "[&.forced-open_.menu-item:hover]:rounded-lg",
  "[&.pinned_.menu-item:hover]:rounded-lg",
  "[&.hover-open_.menu-item:hover]:bg-[#FCFAFF]",
  "[&.forced-open_.menu-item:hover]:bg-[#FCFAFF]",
  "[&.pinned_.menu-item:hover]:bg-[#FCFAFF]",
  "[&.hover-open_.menu-item:hover]:text-[#1a1823]",
  "[&.forced-open_.menu-item:hover]:text-[#1a1823]",
  "[&.pinned_.menu-item:hover]:text-[#1a1823]",
  "[&.hover-open_.menu-item:hover]:shadow-[0_1px_2px_rgba(16,24,40,0.05)]",
  "[&.forced-open_.menu-item:hover]:shadow-[0_1px_2px_rgba(16,24,40,0.05)]",
  "[&.pinned_.menu-item:hover]:shadow-[0_1px_2px_rgba(16,24,40,0.05)]",
  "[&.hover-open_.menu-item:hover_.menu-label]:text-[#1a1823]",
  "[&.forced-open_.menu-item:hover_.menu-label]:text-[#1a1823]",
  "[&.pinned_.menu-item:hover_.menu-label]:text-[#1a1823]",
];

const menuWrapperClassName = [
  ...menuWrapperBaseClasses,
  ...menuWrapperCollapsedClasses,
  ...menuWrapperExpandedClasses,
].join(" ");

function MenuWrapper({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn(menuWrapperClassName, className)} {...props}>
      {children}
    </div>
  );
}
